import { EllipsisTextStyle } from '@/components/atom/text/DefaultText';
import useQnaNativeDefaultViewModel from '@/shared/hooks/useQnaNativeDefaultViewModel';
import React from 'react';
import styled from 'styled-components';

type TagList = Array<{
  name: string;
}>;

const hasTagList = (tagList: TagList) => tagList.length > 0;
const getVisibleTagList = (tagList: TagList) => tagList.slice(0, 2);
const getShouldShowPlusTag = (tagList: TagList) => tagList.length - 2 > 0;

export interface OnClickQuestionProps {
  sectionName?: string;
  questionId: number | string;
}
export interface QuestionItemProps {
  title: string;
  content: string;
  tagList: TagList;
  itemStyle?: React.CSSProperties;
  titleAsH2?: boolean;
  isLast?: boolean;
}

const QnaNativeDefault = ({ title, content, tagList, itemStyle, titleAsH2 = true, isLast }: QuestionItemProps) => {
  const viewModel = useQnaNativeDefaultViewModel();
  return (
    <Container>
      <QuestionWrapper isLast={isLast} onClick={viewModel.onClickQnaNativeDefault} style={itemStyle}>
        <QuestionBody>
          <QuestionBodyContentWrapper hasImage={false}>
            <div>
              <QuestionBodyTitle as={titleAsH2 ? 'h2' : 'h3'}>{title}</QuestionBodyTitle>
              <QuestionBodyContent>{content}</QuestionBodyContent>
            </div>
          </QuestionBodyContentWrapper>
        </QuestionBody>
        <TagsWrapper>
          {hasTagList(tagList) && (
            <>
              {getVisibleTagList(tagList).map((tag) => (
                <Tag key={tag.name}>
                  <TagText as={titleAsH2 ? 'h3' : 'span'}>{tag.name}</TagText>
                </Tag>
              ))}
              {getShouldShowPlusTag(tagList) && (
                <Tag>
                  <TagText>+{tagList.length - 2}</TagText>
                </Tag>
              )}
            </>
          )}
        </TagsWrapper>
        <div className='absolute top-[12px] right-[12px] flex h-[18px] w-[28px] items-center justify-center rounded-[5px] bg-dimmed-20'>
          <p className='typography-ui-12m text-white'>AD</p>
        </div>
      </QuestionWrapper>
    </Container>
  );
};

export default QnaNativeDefault;

const Container = styled.article`
  ${({ theme }) => `
    &:hover {
      background-color: ${theme.color.gray30};
    }

    &:active {
      background-color: ${theme.color.gray50};
    }
    cursor: pointer;
  `};

  &:last-child {
    padding-bottom: 0;
  }
`;

const QuestionWrapper = styled.div<{ isLast?: boolean }>`
  position: relative;
  padding: 24px;
  ${({ theme, isLast }) => `
    border-bottom: ${isLast ? 'none' : `1px solid ${theme.color.gray40}`};
  `};
`;

const QuestionBody = styled.div`
  display: flex;
  margin-bottom: 12px;
`;

const QuestionBodyContentWrapper = styled.div<{
  hasImage?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: ${({ hasImage }) => (hasImage ? 'calc(100% - 56px)' : '100%')};
  padding-right: 10px;
`;

const QuestionBodyTitle = styled.h2`
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;

  ${({ theme }) => `
    ${theme.typography.UI17SB};
    color: ${theme.color.gray800};
  `}
`;

const QuestionBodyContent = styled.p`
  ${EllipsisTextStyle};

  ${({ theme }) => `
    ${theme.typography.UI13R};
    color: ${theme.color.gray500};
    `}
`;

const TagsWrapper = styled.div`
  gap: 6px;
  display: flex;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  padding: 3px 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;

  ${({ theme }) => `
    ${theme.typography.UI13SB};
    color: ${theme.color.gray500};
    background: ${theme.color.gray20};
  `};
`;

const TagText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI13SB};
    color: ${theme.color.gray500};
  `};
`;
